import {useEffect, useRef} from "react";
import {Box} from "@chakra-ui/react";
import {getPixel, setPixel} from "../tabs/images";

const mirroredPoints = (x, y, profile, mirror) => {
  const points = [[x, y]];
  if (mirror === "horizontal" || mirror === "quad") {
    points.push([profile.width - x - 1, y]);
  }
  if (mirror === "vertical" || mirror === "quad") {
    points.push([x, profile.height - y - 1]);
  }
  if (mirror === "quad") {
    points.push([profile.width - x - 1, profile.height - y - 1]);
  }
  return points;
};

export function PixelGrid({
  bitmap,
  profile,
  onChange,
  onStrokeStart,
  onStrokeEnd,
  tool = "draw",
  mirror = "none",
  interactive = true,
}) {
  const gridRef = useRef(null);
  const bitmapRef = useRef(bitmap);
  const drawingRef = useRef(false);
  const lastPoint = useRef("");

  useEffect(() => {
    bitmapRef.current = bitmap;
  }, [bitmap]);

  useEffect(() => {
    const stopDrawing = () => {
      if (drawingRef.current) onStrokeEnd?.(bitmapRef.current);
      drawingRef.current = false;
      lastPoint.current = "";
    };
    window.addEventListener("pointerup", stopDrawing);
    window.addEventListener("pointercancel", stopDrawing);
    return () => {
      window.removeEventListener("pointerup", stopDrawing);
      window.removeEventListener("pointercancel", stopDrawing);
    };
  }, [onStrokeEnd]);

  const paintAtPointer = (event) => {
    if (!interactive || !gridRef.current) return;
    const rect = gridRef.current.getBoundingClientRect();
    const x = Math.floor((event.clientX - rect.left) / (rect.width / profile.width));
    const y = Math.floor((event.clientY - rect.top) / (rect.height / profile.height));
    if (x < 0 || y < 0 || x >= profile.width || y >= profile.height) return;
    const pointKey = `${x}:${y}`;
    if (pointKey === lastPoint.current) return;
    lastPoint.current = pointKey;

    let next = bitmapRef.current;
    mirroredPoints(x, y, profile, mirror).forEach(([mirrorX, mirrorY]) => {
      next = setPixel(next, profile, mirrorX, mirrorY, tool === "draw");
    });
    bitmapRef.current = next;
    onChange(next);
  };

  return (
    <Box
      ref={gridRef}
      role="application"
      aria-label={`${profile.width} by ${profile.height} pixel canvas`}
      onPointerDown={(event) => {
        if (!interactive) return;
        event.preventDefault();
        drawingRef.current = true;
        onStrokeStart?.(bitmapRef.current);
        lastPoint.current = "";
        event.currentTarget.setPointerCapture?.(event.pointerId);
        paintAtPointer(event);
      }}
      onPointerMove={(event) => {
        if (!drawingRef.current || !interactive) return;
        event.preventDefault();
        paintAtPointer(event);
      }}
      onPointerUp={() => {
        if (drawingRef.current) onStrokeEnd?.(bitmapRef.current);
        drawingRef.current = false;
        lastPoint.current = "";
      }}
      display="grid"
      gridTemplateColumns={`repeat(${profile.width}, minmax(0, 1fr))`}
      gap={{base: "1px", md: "2px"}}
      width="100%"
      maxW={`${profile.width * (profile.height > 12 ? 18 : 20)}px`}
      aspectRatio={`${profile.width} / ${profile.height}`}
      p={{base: "7px", md: "11px"}}
      bg="#060708"
      border="1px solid"
      borderColor="#35393e"
      borderRadius="5px"
      boxShadow="inset 0 0 0 1px rgba(0,0,0,.9), 0 20px 55px rgba(0,0,0,.35)"
      touchAction="none"
      cursor={interactive ? "crosshair" : "default"}
      userSelect="none"
    >
      {Array.from({length: profile.height}).flatMap((_, y) => (
        Array.from({length: profile.width}).map((__, x) => {
          const active = getPixel(bitmap, profile, x, y);
          return (
            <Box
              key={`${x}-${y}`}
              bg={active ? "#ffd84d" : "#202327"}
              borderRadius="1px"
              boxShadow={active ? "0 0 9px rgba(255,216,77,.5)" : "inset 0 0 0 1px rgba(255,255,255,.025)"}
              transition="background-color 60ms linear"
              minW={0}
              minH={0}
            />
          );
        })
      ))}
    </Box>
  );
}
